import _ from 'lodash'
import enums from 'scoreboard/components/enums'
import i18n from 'scoreboard/components/i18n'
import twoSteps from './two-steps'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT, CURRENCY, YES_NO, DATE, DATE_TIME, CLUSTER } = DataType

/**
 * @param {Object} options
 *
 * @param {String} [options.parentDomainType] the DomainType of the parent
 * object this filter is associated with. For example, if this is for
 * a chart serie, parentDomainType would be 'chart'.
 *
 * @param {ApiDatasetFilter} [options.filter] the filter that is being edited
 *
 * @param {Boolean} [options.ellipsis=true] whether to include an
 * ellipsis after the operator name. This is used to indicate that there are
 * multiple steps when using filters in a wizard. Filters sometimes aren't in
 * a wizard though so there shouldn't be an ellipsis (#52910)
 */
function operatorOptions (field, opt = { ellipsis: true }) {
	field = _.get(field, 'attributes', field)

	let dataType = field.dataType
	// something like datasetsYesNoFilterOperator, datasetsTextFilterOperator, etc
	let enumKey = 'datasets' + filterOperatorId(dataType) + 'FilterOperator'
	enumKey = remapEnumKey(field, enumKey)

	let options = _.clone(enums[enumKey])
	if (opt.ellipsis) {
		// #10831 add an ellipsis after operator name if there's a second step
		// after selecting the operator
		_.each(options, function(msgKey, operator) {
			var hasTwoSteps = twoSteps(dataType, operator)
			if (hasTwoSteps) {
				options[operator] = i18n.get(msgKey) + '...'
			}
		}.bind(this))
	}
	// if there are no ranges, filter out the range options
	if (_.isEmpty(field.ranges)) {
		options = _.omit(options, ['in-the-range', 'not-in-any-range'])
	}

	//introduced in #35828, limit to specific scenario until confirmed it should
	//be in the default list of date operators
	if (_.isNil(opt.today)) {
		options = _.omit(options, 'today')
	}

	// #16622 not in any period shouldn't be an option
	// https://github.com/SpiderStrategies/Scoreboard/issues/16622#issuecomment-377511429
	// #45658 allow not in any period for calendar period fields
	delete options['not-in-any-period']

	// note opt.parentDomainType is 'dashboard' for all dashboards regardless of whether
	// they are being displayed in the dashboards section, bookmarks, or briefings

	if ((dataType === DATE || dataType === DATE_TIME)
			&& !_.includes(['dashboard', 'chart'], opt.parentDomainType)) {
		delete options['in-period']
	}

	// #24993 "is" rollup operator uses a tooltip and thus only makes sense for
	// dashboards (other sections can just use "is-any")
	if (isGeographicOrRollup(dataType) && opt.parentDomainType !== 'dashboard') {
		delete options['is']
	}

	// #29852 new operators added for transformations,
	// removing here until implemented
	delete options['character-length-between']
	delete options['character-length-greater-than']
	delete options['character-length-less-than']
	delete options['character-length-is-any']
	delete options['character-length-is-not-any']
	delete options['matches-regular-expression']
	// #57800 also remove "is/is not date" and "is/is not number" operators,
	// since they are for transformations only
	delete options['is-date']
	delete options['is-not-date']
	delete options['is-number']
	delete options['is-not-number']

	// #43491 can only select 'in-any-period-index' if the filter already has
	// the breakdown/group calendars set; without those settings, we don't know
	// what pseudo-periods to show
	if (!(opt.filter && opt.filter.groupCalendarId && opt.filter.breakdownCalendarId)) {
		delete options['in-any-period-index']
	}

	return options
}

/**
 * These enum keys come from java class names such as {@link DatasetsTextFilterOperator}
 * but there aren't classes for every data type, and some data types we want to share
 * the same operator options with others (such as cluster types having all the same options
 * as text types)
 */
function remapEnumKey(field, enumKey) {
	if (field.dataType === CLUSTER) {
		return 'datasetsTextFilterOperator'
	}
	if (isGeographicOrRollup(field.dataType)) {
		return 'datasetsRollupFilterOperator'
	}

	return enumKey
}

function filterOperatorId(dataType) {
	// _.camelCase doesn't work as expected
	if (dataType == YES_NO) {
		return 'YesNo'
	}
	if (dataType == CURRENCY || dataType == PERCENT) {
		return 'Number'
	}
	if (isGeographicOrRollup(dataType)) {
		return 'Rollup'
	}
	return _.capitalize(dataType)
}

function defaultOption (field, opt = {}) {
	field = _.get(field, 'attributes', field)

	let firstOption = _.keys(operatorOptions(field, opt))[0]

	if (opt.parentDomainType !== 'dashboard') {
		// just pick the first valid operator for the field type
		return firstOption
	}

	// #24995 use the following defaults for filter widget operators
	if (isGeographicOrRollup(field.dataType)) return 'is'
	switch (field.dataType) {
		case 'yes-no':
			return 'is-any'
		case 'text':
		case 'number':
		case 'percent':
		case 'currency':
			return _.isEmpty(field.ranges) ? 'is-any' : 'in-the-range'
		case 'date':
		case 'date-time':
			return 'in-period'
		case 'time':
			return 'at-any-time'
		default:
			return firstOption
	}
}

export default operatorOptions
export { defaultOption }

operatorOptions.__filename__ = 'datasets/filters/operator-options.js'
